import { useCallback, useRef, useState } from 'react';
import fetch from '@api/fetchers';
import { getGateXeLaunchAPIKey } from '@cont/SeeAll/utils';
import { utils as snackbarUtils } from '@stores/snackbar';

export const GATE_XE_REDIRECT_ERROR = 'Something went wrong, please try again';

type GateXeLaunchResponse = {
  launchUrl?: string;
};

type ApiErrorBody = { error_code?: string; message?: string };

type LaunchApiResult = GateXeLaunchResponse & {
  // The fetcher resolves errors as { error } instead of throwing: the parsed
  // error body for 4xx, or an Error carrying the unread Response for 5xx.
  error?: ApiErrorBody | (Error & { response?: Response });
};

// Shows the API's own `message` for any error_code; falls back to a generic copy.
const getLaunchErrorMessage = async (
  error: LaunchApiResult['error']
): Promise<string> => {
  if (error instanceof Error) {
    try {
      const body: ApiErrorBody = await error.response?.json();
      if (body?.message) return body.message;
    } catch {
      // non-JSON error body
    }
    return GATE_XE_REDIRECT_ERROR;
  }
  return error?.message || GATE_XE_REDIRECT_ERROR;
};

type UseGateXeRedirect = {
  goToTest: () => Promise<void>;
  isRedirecting: boolean;
};

// "Go to test": POSTs the purchased GATE XE SKU to the launch API on click and
// opens the returned launch_url in a new tab. Only one request runs at a time.
const useGateXeRedirect = (skuUid: string | null): UseGateXeRedirect => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const inFlightRef = useRef(false);

  const goToTest = useCallback(async () => {
    if (inFlightRef.current || !skuUid) return;
    inFlightRef.current = true;
    setIsRedirecting(true);

    try {
      const response: LaunchApiResult | undefined = await fetch(
        getGateXeLaunchAPIKey(),
        {
          useAuthToken: true,
          requestType: 'POST',
          body: { sku_uid: skuUid }
        }
      );
      const launchUrl = response?.launchUrl;

      if (!launchUrl) {
        snackbarUtils.open({
          message: await getLaunchErrorMessage(response?.error),
          type: 'error'
        });
        return;
      }

      // Open only once the API has returned the url. If the browser blocks the
      // async popup, fall back to the same tab so the learner still gets there.
      const tab = window.open(launchUrl, '_blank');
      if (tab) {
        tab.opener = null;
      } else {
        window.location.assign(launchUrl);
      }
    } finally {
      inFlightRef.current = false;
      setIsRedirecting(false);
    }
  }, [skuUid]);

  return { goToTest, isRedirecting };
};

export default useGateXeRedirect;
